import * as stylex from "@stylexjs/stylex";
import { Image } from "lucide-react";
import { copy } from "@/copy";
import type { PhotoVariant } from "@/data/repos";
import type { Breakpoint } from "@/domain/breakpoint";
import type { Photo } from "@/domain/schemas";
import { type Clock, clockAt } from "@/domain/time";
import type { ZoneState } from "@/domain/zoneState";
import "@/theme/tokens.stylex";
import { radii, space } from "@/theme/tokens.stylex";
import { ColorBlock } from "@/ui/ColorBlock";
import { Skeleton, SkeletonBlock } from "@/ui/Skeleton";
import { SourceFailure } from "@/ui/SourceFailure";
import { Zone } from "@/ui/Zone";
import { photosLayout } from "../layout";
import { type UploadTile, uploadSummary } from "../uploads";
import type { QrShown } from "./AddFromPhone";
import { FeaturedPhoto, FeaturedWaiting } from "./FeaturedPhoto";
import { PhotoPanel } from "./PhotoPanel";
import { PhotosEmpty } from "./PhotosEmpty";
type PhotosViewProps = {
  state: ZoneState<Photo>;
  retryIn: number;
  onRetrySync: () => void;
  clock: Clock;
  tz: string;
  thisYear: number;
  selected: number;
  playing: boolean;
  uploads: readonly UploadTile[];
  online: boolean;
  qr?: QrShown;
  deleting: boolean;
  breakpoint: Breakpoint;
  backgroundId: string | undefined;
  photoSrc: (photoId: string, variant: PhotoVariant) => string;
  onSelect: (index: number) => void;
  onEdit: (photoId: string) => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: (photoId: string) => void;
  onStep: (delta: number) => void;
  onTogglePlay: () => void;
  onFullScreen: () => void;
  onUpload: () => void;
  onChooseFiles: () => void;
  onCloseQr: () => void;
  onRetry: (id: string) => void;
  onRetryAll: () => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  onSetBackground: (photoId: string) => void;
  onOpenBackground: () => void;
};
type GalleryProps = Omit<PhotosViewProps, "state" | "retryIn" | "onRetrySync"> & {
  photos: readonly Photo[];
  updatedAt?: string;
};
const SKELETON_THUMBS = [0, 1, 2, 3];
const styles = {
  grid: {
    k1xSpc: "xrvj5dj",
    kOIVth: "x1ur044a",
    $$css: true
  },
  featured: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1ur044a",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  skeletonThumbs: {
    k1xSpc: "xrvj5dj",
    kOIVth: "xddi9bj",
    kfiyM8: "x10ukxgv",
    $$css: true
  }
};
export function PhotosView({
  state,
  retryIn,
  onRetrySync,
  ...gallery
}: PhotosViewProps) {
  return <Zone state={state} loading={<PhotosSkeleton breakpoint={gallery.breakpoint} />} empty={gallery.uploads.length > 0 ? <PhotoGallery {...gallery} photos={[]} /> : <PhotosEmpty online={gallery.online} {...gallery.qr && {
    qr: gallery.qr
  }} onUpload={gallery.onUpload} onChooseFiles={gallery.onChooseFiles} />} failed={<SourceFailure icon={Image} title={copy.photos.unavailable} body={copy.zone.failedBody(retryIn)} retryLabel={copy.zone.retry} onRetry={onRetrySync} />}>
      {(photos, updatedAt) => <PhotoGallery {...gallery} photos={photos} {...updatedAt !== undefined && {
      updatedAt
    }} />}
    </Zone>;
}
function PhotosSkeleton({
  breakpoint
}: {
  breakpoint: Breakpoint;
}) {
  const layout = photosLayout(breakpoint);
  return <Skeleton label={copy.photos.loading} xstyle={[styles.grid, layout.grid]}>
      <SkeletonBlock height="100%" radius={radii.lg} xstyle={layout.featured} />
      <div {...stylex.props(styles.skeletonThumbs, layout.thumbs)}>
        {SKELETON_THUMBS.map(thumb => <SkeletonBlock key={thumb} height={160} radius={radii.md} />)}
      </div>
    </Skeleton>;
}
function PhotoGallery({
  photos,
  selected,
  breakpoint,
  photoSrc,
  updatedAt,
  ...rest
}: GalleryProps) {
  const layout = photosLayout(breakpoint);
  const featured = photos[selected];
  return <div {...stylex.props(styles.grid, layout.grid)}>
      <ColorBlock tone="soft" padding="24px" xstyle={styles.featured}>
        {featured ? <FeaturedPhoto photo={featured} src={photoSrc(featured.id, "display")} thisYear={rest.thisYear} playing={rest.playing} isBackground={featured.id === rest.backgroundId} online={rest.online} deleting={rest.deleting} onEdit={() => rest.onEdit(featured.id)} onAskDelete={rest.onAskDelete} onCancelDelete={rest.onCancelDelete} onDelete={() => rest.onDelete(featured.id)} onStep={rest.onStep} onTogglePlay={rest.onTogglePlay} onFullScreen={rest.onFullScreen} onSetBackground={() => rest.onSetBackground(featured.id)} onOpenBackground={rest.onOpenBackground} frame={layout.featured} /> : <FeaturedWaiting frame={layout.featured} />}
      </ColorBlock>
      <PhotoPanel photos={photos} thisYear={rest.thisYear} {...updatedAt !== undefined && {
      updated: clockAt(updatedAt, rest.clock, rest.tz)
    }} selected={selected} uploads={rest.uploads} summary={uploadSummary(rest.uploads, rest.online)} online={rest.online} {...rest.qr && {
      qr: rest.qr
    }} columns={layout.thumbs} frame={layout.panel} photoSrc={photoSrc} onSelect={rest.onSelect} onUpload={rest.onUpload} onChooseFiles={rest.onChooseFiles} onCloseQr={rest.onCloseQr} onRetry={rest.onRetry} onRetryAll={rest.onRetryAll} onRemove={rest.onRemove} onClear={rest.onClear} />
    </div>;
}